/**
 * Feature flag service (deploy != release).
 *
 * Mirrors the backend django-waffle flags so the UI can hide in-progress,
 * flag-gated features until they are turned on. Code for an incomplete feature
 * ships to production disabled; flipping the flag (per-everyone / per-user /
 * percentage) is a separate decision made in the Django admin.
 *
 * See ADR-0014 (web release process) and backend `views_feature_flags.py`.
 *
 * Usage:
 *   import featureFlags from '@/services/featureFlags';
 *   await featureFlags.load();
 *   if (featureFlags.isEnabled('new_consult_ui')) { ... }
 */
import api from './api';

const state = {
  flags: {},
  switches: {},
  loaded: false,
  loadingPromise: null,
};

export default {
  /**
   * Fetch the active flags/switches for the current user. Cached after the
   * first call; pass force=true to refresh (e.g. after login).
   * @param {boolean} [force]
   * @returns {Promise<void>}
   */
  async load(force = false) {
    if (state.loaded && !force) return;
    if (state.loadingPromise && !force) return state.loadingPromise;

    state.loadingPromise = api
      .get('/feature-flags/')
      .then((response) => {
        state.flags = response.data?.flags || {};
        state.switches = response.data?.switches || {};
        state.loaded = true;
      })
      .catch(() => {
        // Fail safe: on error, treat everything as OFF (hide unreleased work).
        state.flags = {};
        state.switches = {};
        state.loaded = true;
      })
      .finally(() => {
        state.loadingPromise = null;
      });

    return state.loadingPromise;
  },

  /**
   * @param {string} name
   * @returns {boolean} whether the flag is active for the current user
   */
  isEnabled(name) {
    return state.flags[name] === true;
  },

  /**
   * @param {string} name
   * @returns {boolean} whether a global switch is on
   */
  isSwitchOn(name) {
    return state.switches[name] === true;
  },

  /** @returns {{flags: Object, switches: Object, loaded: boolean}} */
  all() {
    return { flags: { ...state.flags }, switches: { ...state.switches }, loaded: state.loaded };
  },
};
